import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTabBarVisibility } from '../../contexts/TabBarVisibilityContext';
import { useBrowserBackHandlerRef } from '../../contexts/BrowserBackHandlerRefContext';
import Button from '../../components/Button';
import PhotoGalleryModal from '../../components/PhotoGalleryModal';
import {
  escalateSupportTicket,
  getSupportArchivedTickets,
  getSupportChatMessages,
  getSupportChatTickets,
  getSupportFaqById,
  pollSupportChat,
  sendSupportPrecloseDecision,
  selectSupportFaq,
  sendSupportChatMessageWithFiles,
  fetchSupportChatMediaBlob,
} from '../../api/support';
import { compressImage } from '../../utils/compressImage';
import { LinkifiedMessageText } from '../../utils/linkifyMessageText';
import './SupportChatPage.css';

function Bubble({ message, onSelectFaq, onEscalate, onPrecloseDecision, onOpenGallery, resolveMediaSrc, faqTitles }) {
  const sender = message.sender_type || 'system';
  const cls =
    sender === 'user'
      ? 'support-chat-page__bubble support-chat-page__bubble--user'
      : sender === 'agent'
        ? 'support-chat-page__bubble support-chat-page__bubble--agent'
        : 'support-chat-page__bubble support-chat-page__bubble--bot';
  const meta = message.meta_json || null;
  const faqIds = Array.isArray(meta?.faq_ids) ? meta.faq_ids : [];
  const media = Array.isArray(message?.media_ids) ? message.media_ids : [];
  const mediaPaths = media
    .map((m) => String(m || '').trim())
    .filter(Boolean);
  return (
    <div className={cls}>
      <LinkifiedMessageText text={message.text || ''} className="support-chat-page__bubble-text" />
      {mediaPaths.length ? (
        <div className="support-chat-page__bubble-media">
          {mediaPaths.map((path, idx) => (
            <button
              key={`${message.id}-m-${idx}`}
              type="button"
              className="support-chat-page__bubble-media-link"
              onClick={() => onOpenGallery(mediaPaths, idx)}
              aria-label={`Открыть фото ${idx + 1}`}
            >
              <img src={resolveMediaSrc(path)} alt="" className="support-chat-page__bubble-media-img" />
            </button>
          ))}
        </div>
      ) : null}
      {faqIds.length ? (
        <div className="support-chat-page__bubble-meta">
          {faqIds.map((id) => (
            <button
              key={id}
              type="button"
              className="support-chat-page__meta-btn"
              onClick={() => onSelectFaq(id)}
            >
              {faqTitles[id] || `FAQ #${id}`}
            </button>
          ))}
        </div>
      ) : null}
      {meta?.type === 'offer_escalation' ? (
        <div className="support-chat-page__bubble-meta">
          <button type="button" className="support-chat-page__meta-btn" onClick={onEscalate}>
            Позвать оператора
          </button>
        </div>
      ) : null}
      {meta?.type === 'preclose_prompt' ? (
        <div className="support-chat-page__bubble-meta">
          <button type="button" className="support-chat-page__meta-btn" onClick={() => onPrecloseDecision(false)}>
            Да, закрыть
          </button>
          <button type="button" className="support-chat-page__meta-btn" onClick={() => onPrecloseDecision(true)}>
            Продолжить чат
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function SupportChatPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const browserBackHandlerRef = useBrowserBackHandlerRef();
  const { setTabBarVisible } = useTabBarVisibility();
  const historySeededRef = useRef(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [archivedTickets, setArchivedTickets] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeTicketId, setActiveTicketId] = useState(null);
  const [ticketStatus, setTicketStatus] = useState('');
  const [archivedMode, setArchivedMode] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [faqTitles, setFaqTitles] = useState({});
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [photoGallery, setPhotoGallery] = useState(null);
  const [mediaBlobUrls, setMediaBlobUrls] = useState({});
  const scrollerRef = useRef(null);
  const bootLoadedRef = useRef(false);
  const pollAfterIdRef = useRef(0);
  const fileInputRef = useRef(null);

  const lastMessageId = useMemo(
    () => (messages.length ? Number(messages[messages.length - 1]?.id || 0) : 0),
    [messages]
  );

  const ensureFaqTitles = useCallback(async (ids) => {
    const uniq = Array.from(new Set((ids || []).map((x) => Number(x)).filter((x) => x > 0)));
    const pending = uniq.filter((id) => !faqTitles[id]);
    if (!pending.length) return;
    const entries = await Promise.all(
      pending.map(async (id) => {
        try {
          const faq = await getSupportFaqById(id);
          return [id, faq?.title || `FAQ #${id}`];
        } catch {
          return [id, `FAQ #${id}`];
        }
      })
    );
    setFaqTitles((prev) => {
      const next = { ...prev };
      for (const [id, title] of entries) next[id] = title;
      return next;
    });
  }, [faqTitles]);

  const appendMessages = useCallback(
    async (incoming) => {
      if (!Array.isArray(incoming) || !incoming.length) return;
      const faqIds = [];
      for (const m of incoming) {
        const arr = Array.isArray(m?.meta_json?.faq_ids) ? m.meta_json.faq_ids : [];
        for (const id of arr) faqIds.push(id);
      }
      if (faqIds.length) {
        await ensureFaqTitles(faqIds);
      }
      setMessages((prev) => {
        const seen = new Set(prev.map((x) => Number(x.id)));
        const merged = [...prev];
        let hasNew = false;
        for (const m of incoming) {
          const id = Number(m?.id || 0);
          if (id && !seen.has(id)) {
            seen.add(id);
            merged.push(m);
            hasNew = true;
          }
        }
        if (!hasNew) return prev;
        merged.sort((a, b) => Number(a.id || 0) - Number(b.id || 0));
        pollAfterIdRef.current = Number(merged[merged.length - 1]?.id || 0);
        return merged;
      });
    },
    [ensureFaqTitles]
  );

  const loadArchived = useCallback(async () => {
    setLoadingArchive(true);
    try {
      const rows = await getSupportArchivedTickets();
      setArchivedTickets(Array.isArray(rows) ? rows : []);
    } catch {
      setArchivedTickets([]);
    } finally {
      setLoadingArchive(false);
    }
  }, []);

  const openArchivedTicket = useCallback(
    async (ticketId) => {
      setDrawerOpen(false);
      setError('');
      try {
        const rows = await getSupportChatMessages(ticketId, 0);
        setActiveTicketId(Number(ticketId));
        setTicketStatus('закрыт');
        setArchivedMode(true);
        const list = Array.isArray(rows) ? rows : [];
        setMessages(list);
        pollAfterIdRef.current = Number(list[list.length - 1]?.id || 0);
      } catch (e) {
        setError(e.message || 'Не удалось открыть архив');
      }
    },
    []
  );

  const openActiveTicket = useCallback(async () => {
    setError('');
    try {
      const rows = await getSupportChatTickets({ includeArchived: false, archivedOnly: false });
      const list = Array.isArray(rows) ? rows : [];
      if (!list.length) {
        setActiveTicketId(null);
        setTicketStatus('');
        setArchivedMode(false);
        setMessages([]);
        pollAfterIdRef.current = 0;
        return;
      }
      const ticket = list[0];
      const ticketId = Number(ticket?.id || 0);
      if (!ticketId) {
        setActiveTicketId(null);
        setTicketStatus('');
        setArchivedMode(false);
        setMessages([]);
        pollAfterIdRef.current = 0;
        return;
      }
      const initial = await getSupportChatMessages(ticketId, 0);
      setActiveTicketId(ticketId);
      setTicketStatus(String(ticket?.status || ''));
      setArchivedMode(false);
      const listMsgs = Array.isArray(initial) ? initial : [];
      setMessages(listMsgs);
      pollAfterIdRef.current = Number(listMsgs[listMsgs.length - 1]?.id || 0);
    } catch (e) {
      setError(e.message || 'Не удалось загрузить активный чат');
    }
  }, []);

  const backToNewChat = useCallback(() => {
    void openActiveTicket();
  }, [openActiveTicket]);

  const send = useCallback(async () => {
    const payloadText = (text || '').trim();
    if ((!payloadText && pendingPhotos.length === 0) || sending || archivedMode) return;
    setSending(true);
    setError('');
    try {
      const data = await sendSupportChatMessageWithFiles({
        ticket_id: activeTicketId || undefined,
        text: payloadText,
        files: pendingPhotos.map((x) => x.file),
      });
      if (data?.ticket_id) {
        setActiveTicketId(Number(data.ticket_id));
      }
      await appendMessages(data?.messages || []);
      setTicketStatus(String(data?.status || ''));
      setArchivedMode(String(data?.status || '').toLowerCase() === 'закрыт');
      setText('');
      setPendingPhotos((prev) => {
        prev.forEach((x) => URL.revokeObjectURL(x.previewUrl));
        return [];
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      setError(e.message || 'Не удалось отправить сообщение');
    } finally {
      setSending(false);
    }
  }, [text, pendingPhotos, sending, archivedMode, activeTicketId, appendMessages]);

  const onSelectFaq = useCallback(
    async (faqId) => {
      if (!activeTicketId) return;
      setError('');
      try {
        const data = await selectSupportFaq(activeTicketId, faqId, lastMessageId);
        await appendMessages(data?.messages || []);
      } catch (e) {
        setError(e.message || 'Не удалось открыть ответ FAQ');
      }
    },
    [activeTicketId, lastMessageId, appendMessages]
  );

  const onEscalate = useCallback(async () => {
    if (!activeTicketId) return;
    setError('');
    try {
      const data = await escalateSupportTicket(activeTicketId, lastMessageId);
      await appendMessages(data?.messages || []);
    } catch (e) {
      setError(e.message || 'Не удалось позвать оператора');
    }
  }, [activeTicketId, lastMessageId, appendMessages]);

  const onPrecloseDecision = useCallback(
    async (continueChat) => {
      if (!activeTicketId) return;
      setError('');
      try {
        const data = await sendSupportPrecloseDecision(activeTicketId, continueChat, lastMessageId);
        await appendMessages(data?.messages || []);
        setTicketStatus(String(data?.status || ''));
        setArchivedMode(String(data?.status || '').toLowerCase() === 'закрыт');
      } catch (e) {
        setError(e.message || 'Не удалось обработать решение по чату');
      }
    },
    [activeTicketId, lastMessageId, appendMessages]
  );

  const handleLeaveSupportChat = useCallback(() => {
    if (location.state?.fromProfile) {
      navigate(-1);
      return;
    }
    navigate('/main/profile');
  }, [location.state, navigate]);

  useLayoutEffect(() => {
    if (historySeededRef.current || location.state?.fromProfile) return;
    historySeededRef.current = true;
    navigate('/main/profile', { replace: true, state: { supportBackSeed: true } });
    navigate('/main/profile/support', { state: { fromProfile: true } });
  }, [location.state, navigate]);

  useEffect(() => {
    browserBackHandlerRef.current = handleLeaveSupportChat;
    return () => {
      if (browserBackHandlerRef.current === handleLeaveSupportChat) {
        browserBackHandlerRef.current = null;
      }
    };
  }, [browserBackHandlerRef, handleLeaveSupportChat]);

  useEffect(() => {
    setTabBarVisible(false);
    const tg = window.Telegram?.WebApp;
    const backButton = tg?.BackButton;
    if (!backButton) return () => setTabBarVisible(true);
    backButton.onClick(handleLeaveSupportChat);
    backButton.show();
    return () => {
      backButton.offClick(handleLeaveSupportChat);
      backButton.hide();
      setTabBarVisible(true);
    };
  }, [handleLeaveSupportChat, setTabBarVisible]);

  useEffect(() => {
    if (bootLoadedRef.current) return;
    bootLoadedRef.current = true;
    void openActiveTicket();
  }, [openActiveTicket]);

  useEffect(() => {
    if (!activeTicketId || archivedMode) return undefined;
    let cancelled = false;
    const loop = async () => {
      while (!cancelled) {
        try {
          const data = await pollSupportChat(activeTicketId, pollAfterIdRef.current, 25);
          if (cancelled) return;
          const msgs = Array.isArray(data?.messages) ? data.messages : [];
          if (msgs.length) {
            await appendMessages(msgs);
          }
          setTicketStatus(String(data?.status || ''));
          setArchivedMode(String(data?.status || '').toLowerCase() === 'закрыт');
        } catch {
          await new Promise((r) => setTimeout(r, 1200));
        }
      }
    };
    void loop();
    return () => {
      cancelled = true;
    };
  }, [activeTicketId, archivedMode, appendMessages]);

  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages.length]);

  useEffect(
    () => () => {
      pendingPhotos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      Object.values(mediaBlobUrls).forEach((u) => {
        try { URL.revokeObjectURL(u); } catch {}
      });
    },
    [pendingPhotos, mediaBlobUrls]
  );

  const readOnlyMode =
    archivedMode || String(ticketStatus || '').toLowerCase() === 'предзакрытие';
  const openGallery = useCallback((mediaPaths, currentIndex) => {
    const photos = (Array.isArray(mediaPaths) ? mediaPaths : [])
      .map((m) => String(m || '').trim())
      .filter(Boolean)
      .map((m) => resolveMediaSrc(m));
    if (!photos.length) return;
    setPhotoGallery({ photos, currentIndex: Number(currentIndex || 0) });
  }, [mediaBlobUrls]);

  const resolveMediaSrc = useCallback(
    (mediaId) => {
      const id = String(mediaId || '').trim();
      if (!id) return '';
      const val = mediaBlobUrls[id];
      return typeof val === 'string' && val ? val : '';
    },
    [mediaBlobUrls]
  );

  useEffect(() => {
    let cancelled = false;
    const allIds = new Set();
    messages.forEach((m) => {
      (Array.isArray(m?.media_ids) ? m.media_ids : []).forEach((id) => {
        const s = String(id || '').trim();
        if (s) allIds.add(s);
      });
    });
    const missing = Array.from(allIds).filter((id) => !(id in mediaBlobUrls));
    if (!missing.length) return;
    (async () => {
      const next = {};
      for (const id of missing) {
        try {
          const blob = await fetchSupportChatMediaBlob(id);
          if (cancelled) return;
          next[id] = URL.createObjectURL(blob);
        } catch {
          // Фиксируем как обработанный id, чтобы не спамить повторными запросами.
          next[id] = '__error__';
        }
      }
      if (cancelled) return;
      setMediaBlobUrls((prev) => ({ ...prev, ...next }));
    })();
    return () => {
      cancelled = true;
    };
  }, [messages, mediaBlobUrls]);

  const handleComposerKeyDown = useCallback(
    (e) => {
      if (e.key !== 'Enter') return;
      if (e.shiftKey) return;
      // Desktop UX: Enter отправляет, Shift+Enter переносит строку.
      e.preventDefault();
      if (!readOnlyMode && !sending) {
        void send();
      }
    },
    [readOnlyMode, sending, send]
  );

  return (
    <div className="support-chat-page page-container">
      <div className="support-chat-page__topbar">
        <Button
          type="button"
          variant="secondary"
          size="small"
          className="support-chat-page__archive-btn"
          onClick={() => {
            setDrawerOpen(true);
            void loadArchived();
          }}
        >
          Архив
        </Button>
        <div className="support-chat-page__header">
          <div className="support-chat-page__header-text">Чат с поддержкой</div>
        </div>
      </div>

      {archivedMode ? (
        <div className="support-chat-page__archived-note">
          <span>Это архивный чат. Новые сообщения недоступны.</span>
          <button type="button" className="support-chat-page__return-btn" onClick={backToNewChat}>
            К новому чату
          </button>
        </div>
      ) : null}

      <div className="support-chat-page__messages" ref={scrollerRef}>
        {messages.length === 0 ? (
          <p className="support-chat-page__empty">
            Новый чат. Опишите проблему, и бот попробует помочь или позовёт оператора.
          </p>
        ) : (
          messages.map((m) => (
            <Bubble
              key={m.id}
              message={m}
              onSelectFaq={onSelectFaq}
              onEscalate={onEscalate}
              onPrecloseDecision={onPrecloseDecision}
              onOpenGallery={openGallery}
              resolveMediaSrc={resolveMediaSrc}
              faqTitles={faqTitles}
            />
          ))
        )}
      </div>

      {error ? <p className="support-chat-page__error">{error}</p> : null}

      <div className="support-chat-page__composer">
        {pendingPhotos.length ? (
          <div className="support-chat-page__pending-media">
            {pendingPhotos.map((p) => (
              <div key={p.id} className="support-chat-page__pending-media-item">
                <img src={p.previewUrl} alt="" className="support-chat-page__pending-media-img" />
                <button
                  type="button"
                  className="support-chat-page__pending-media-remove"
                  onClick={() =>
                    setPendingPhotos((prev) => {
                      const next = prev.filter((x) => x.id !== p.id);
                      URL.revokeObjectURL(p.previewUrl);
                      return next;
                    })
                  }
                  aria-label="Убрать фото"
                  disabled={sending}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : null}
        <div className="support-chat-page__composer-row">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="support-chat-page__file-input"
            onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              if (!files.length) return;
              try {
                const room = Math.max(0, 3 - pendingPhotos.length);
                if (room <= 0) {
                  setError('Можно прикрепить не более 3 фото');
                  return;
                }
                const selected = files.slice(0, room);
                if (!selected.length) return;
                const prepared = [];
                for (const f of selected) {
                  const blob = await compressImage(f, { maxSize: 1400, quality: 0.82 });
                  const jpgFile = new File([blob], `${Date.now()}-${Math.random().toString(36).slice(2)}.jpg`, {
                    type: 'image/jpeg',
                  });
                  prepared.push({
                    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                    file: jpgFile,
                    previewUrl: URL.createObjectURL(jpgFile),
                  });
                }
                setPendingPhotos((prev) => [...prev, ...prepared].slice(0, 3));
              } catch {
                setError('Не удалось подготовить изображения');
              } finally {
                if (fileInputRef.current) fileInputRef.current.value = '';
              }
            }}
            disabled={readOnlyMode || sending || pendingPhotos.length >= 3}
          />
          <button
            type="button"
            className="support-chat-page__attach"
            onClick={() => fileInputRef.current?.click()}
            disabled={readOnlyMode || sending || pendingPhotos.length >= 3}
            aria-label="Добавить фото"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M7 7H5V19H19V17H21V19C21 20.1 20.1 21 19 21H5C3.9 21 3 20.1 3 19V7C3 5.9 3.9 5 5 5H7V7ZM17 3V5H14V8H12V5H9V3H12V0H14V3H17ZM8 17H16L13.5 13.5L11.5 16L10 14L8 17Z" fill="currentColor" />
            </svg>
          </button>
        <textarea
          className="support-chat-page__input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleComposerKeyDown}
          placeholder={readOnlyMode ? 'Чат недоступен для ввода' : 'Напишите сообщение…'}
          disabled={readOnlyMode || sending}
        />
        <button
          type="button"
          className="support-chat-page__send"
          onClick={() => void send()}
          disabled={readOnlyMode || sending}
          aria-label="Отправить сообщение"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M3.4 20.6L21 12L3.4 3.4L3.39 10.09L16 12L3.39 13.9L3.4 20.6Z" fill="currentColor" />
          </svg>
        </button>
        </div>
      </div>

      {drawerOpen ? (
        <>
          <div className="support-chat-page__drawer-backdrop" onClick={() => setDrawerOpen(false)} />
          <aside className="support-chat-page__drawer" role="dialog" aria-label="Архивные чаты">
            <div className="support-chat-page__drawer-title">Архивные чаты</div>
            <Button type="button" variant="secondary" size="small" className="support-chat-page__drawer-new-btn" onClick={backToNewChat}>
              Новый чат
            </Button>
            <ul className="support-chat-page__drawer-list">
              {loadingArchive ? (
                <li className="support-chat-page__drawer-item-text">Загрузка…</li>
              ) : archivedTickets.length === 0 ? (
                <li className="support-chat-page__drawer-item-text">Пока нет архивных чатов</li>
              ) : (
                archivedTickets.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      className="support-chat-page__drawer-item-btn"
                      onClick={() => void openArchivedTicket(t.id)}
                    >
                      <div className="support-chat-page__drawer-item-title">Тикет #{t.id}</div>
                      <div className="support-chat-page__drawer-item-text">
                        {t.first_message_text || t.last_message_text || 'Без текста'}
                      </div>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </aside>
        </>
      ) : null}
      {photoGallery ? (
        <PhotoGalleryModal
          photos={photoGallery.photos}
          currentIndex={photoGallery.currentIndex}
          onClose={() => setPhotoGallery(null)}
        />
      ) : null}
    </div>
  );
}

